/**
 * Generic hook to capture page state before navigation
 * Captures both widget pagination states and layout pagination state
 * Generic for all PL pages
 * Uses injectable cache manager for flexibility across page types
 */

import { useRouter } from 'next/router'
import { useEffect, useRef, useMemo } from 'react'
import { useStore } from 'zustand'

import {
  usePageStateStore,
  type LayoutPaginationState,
} from '@src/components/pl-base-widget/contexts/page-state-context'
import { usePLPageMeta } from '@src/components/pl-page-context'
import { getCollectionCacheKey } from '@src/pages/[...slug]/components/collection/utils'
import { getUnlockedScrollY } from '@src/quince-components/src/atoms/modal/hooks/useLockBodyScroll'
import { runWhenMainThreadIdle } from '@src/quince-site-common/src/utils/interaction-scheduling'

import { defaultPaginationCacheAdapter } from '../utils/pagination-cache-adapters'

import type { PaginationCacheManager } from '../contracts/pagination-cache'
import type { CacheKeyGenerator } from '../contracts/scroll-cache'

export interface UsePLPageStateCaptureOptions {
  /**
   * Cache key generator function
   * If not provided, uses pageMeta.paginationCacheKeyGenerator or defaults to getCollectionCacheKey
   */
  cacheKeyGenerator?: CacheKeyGenerator
  /**
   * Pagination cache manager
   * If not provided, uses pageMeta.paginationCacheManager or defaults to collection cache adapter
   */
  cacheManager?: PaginationCacheManager
}

/**
 * Generic hook to capture page state before navigation
 * Captures widget pagination states, layout pagination state, and scroll position
 * Should be used in PL page components within PLPageProvider
 *
 * @param options - Optional configuration for cache key generation and cache manager
 * @example
 * // Default (collection pages)
 * usePLPageStateCapture()
 *
 * // Search pages with custom cache
 * usePLPageStateCapture({
 *   cacheKeyGenerator: getSearchCacheKey,
 *   cacheManager: searchPaginationCacheAdapter
 * })
 */
export function usePLPageStateCapture(options?: UsePLPageStateCaptureOptions) {
  const router = useRouter()
  const pageMeta = usePLPageMeta()
  const pageStateStore = usePageStateStore()
  const getAllWidgetStates = useStore(pageStateStore, state => state.widgetStateActions.getAllWidgetStates)
  const getLayoutPaginationState = useStore(
    pageStateStore,
    state => state.layoutPaginationActions.getLayoutPaginationState,
  )

  // Use provided options, pageMeta config, or defaults (priority: options > pageMeta > defaults)
  const cacheKeyGenerator = options?.cacheKeyGenerator ?? pageMeta.paginationCacheKeyGenerator ?? getCollectionCacheKey
  const cacheManager = options?.cacheManager ?? pageMeta.paginationCacheManager ?? defaultPaginationCacheAdapter

  // Track scroll position
  const scrollY = useRef(0)

  // Generate cache key using provided generator
  const cacheKey = useMemo(() => {
    return cacheKeyGenerator(pageMeta.pathname, pageMeta.searchParams)
  }, [pageMeta.pathname, pageMeta.searchParams, cacheKeyGenerator])

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      // Read through getUnlockedScrollY: opening a body-locking modal (Quick Add) pins <body> with
      // position:fixed, which fires a scroll event reporting 0 and would overwrite the real position
      // here. The navigation that follows then saves 0 and Back lands at the top. [STRFRNT-15288]
      scrollY.current = getUnlockedScrollY()
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Capture state before navigation
  useEffect(() => {
    const handleRouteChangeStart = () => {
      try {
        // Get all widget states from store
        const widgetStates = getAllWidgetStates()

        // Get layout pagination state from store
        const layoutPaginationState = getLayoutPaginationState()

        // Convert layout pagination state to cache format if it has data
        const layoutStateForCache: LayoutPaginationState | undefined =
          layoutPaginationState.accumulatedWidgets.length > 0 ? layoutPaginationState : undefined

        // Save to cache with scroll position using injectable cache manager
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Using || for boolean OR, not nullish coalescing
        if (Object.keys(widgetStates).length > 0 || layoutStateForCache || scrollY.current > 0) {
          const scrollPosition = scrollY.current
          // publishCacheWithStates JSON.stringifies the whole cache and routeChangeStart fires
          // inside the navigating click — defer the write off the interaction; the cache is only
          // read on a later back-navigation. Runs in a later task, so it needs its own try/catch.
          runWhenMainThreadIdle(() => {
            try {
              cacheManager.publishCacheWithStates(cacheKey, scrollPosition, widgetStates, layoutStateForCache)
            } catch (error) {
              console.error('Failed to capture page state before navigation:', error)
            }
          })
        }
      } catch (error) {
        // Log error but don't block navigation
        console.error('Failed to capture page state before navigation:', error)
      }
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [router.events, cacheKey, getAllWidgetStates, getLayoutPaginationState, cacheManager])
}
